import React from 'react';
import { Modal, Form, InputNumber, Button, Row, Col, Input } from 'antd';

const UpdateVitalsModal = ({ visible, onCancel, onSave, initialVitals = {} }) => {
  const [form] = Form.useForm();

  const handleSave = () => {
    form
      .validateFields()
      .then(values => {
        // console.log('Saving new vitals:', values.vitals);
        onSave(values.vitals); // Pass the vitals object
        form.resetFields(); // Reset after save
      })
      .catch(info => {
        console.log('Validate Failed:', info);
      });
  };

  // Helper function to clean vitals data - extract numeric values from objects
  const cleanVitalsData = (vitals) => {
    if (!vitals) return {};
    
    const cleaned = {};
    Object.keys(vitals).forEach(key => {
      const value = vitals[key];
      if (value === null || value === undefined) {
        cleaned[key] = value;
      } else if (typeof value === 'object' && value.hasOwnProperty('value')) {
        // Extract numeric value from object structure like { value: 120, unit: "mmHg" }
        cleaned[key] = typeof value.value === 'number' ? value.value : null;
      } else if (typeof value === 'number') {
        // Already a number, keep as is
        cleaned[key] = value;
      } else {
        // Try to parse as number, fallback to null
        const parsed = parseFloat(value);
        cleaned[key] = isNaN(parsed) ? null : parsed;
      }
    });
    return cleaned;
  };

  // Set initial values when modal opens or initialVitals change
  React.useEffect(() => {
    console.log("UpdateVitalsModal useEffect - visible:", visible, "initialVitals:", initialVitals); // Add log
    if (visible && initialVitals && Object.keys(initialVitals).length > 0) { // Check if initialVitals has keys
      // Clean the vitals data to ensure proper numeric values
      const cleanedVitals = cleanVitalsData(initialVitals);
      console.log("UpdateVitalsModal: Cleaned vitals data:", cleanedVitals); // Debug log
      
      // Directly set the fields based on the cleaned initialVitals object structure
      // The name prop in Form.Item uses array path ['vitals', 'fieldName']
      // So, setFieldsValue needs { vitals: { fieldName: value } } structure
      form.setFieldsValue({ vitals: cleanedVitals });
    } else if (visible) {
      // If visible but no initialVitals, reset the form
      form.resetFields();
    }
  }, [visible, initialVitals]); // Remove 'form' from dependency array


  return (
    <Modal
      title="Update Current Encounter Vitals"
      visible={visible}
      onCancel={onCancel}
      onOk={handleSave} // Use onOk for the default Save button
      okText="Save Vitals"
      cancelText="Cancel"
      destroyOnClose // Reset form state when closed
    >
      <Form form={form} layout="vertical" name="update_vitals_form">
        {/* Keep layout similar to PatientEncounters modal - Reordered */}
        <Row gutter={16}>
          {/* BP */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item label="BP (Sys/Dia)">
              <Input.Group compact>
                <Form.Item name={['vitals', 'bloodPressureSystolic']} noStyle>
                  <InputNumber style={{ width: '50%' }} placeholder="Sys" />
                </Form.Item>
                <Form.Item name={['vitals', 'bloodPressureDiastolic']} noStyle>
                  <InputNumber style={{ width: '50%' }} placeholder="Dia" />
                </Form.Item>
              </Input.Group>
            </Form.Item>
          </Col>
          {/* Pulse (HR) */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'heartRate']} label="HR (bpm)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {/* Respiratory Rate (RR) */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'respiratoryRate']} label="RR">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {/* Oxygen Saturation (SpO2) */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'oxygenSaturation']} label="SpO2 (%)">
              <InputNumber style={{ width: '100%' }} min={0} max={100} />
            </Form.Item>
          </Col>
          {/* Temperature */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'temperature']} label="Temp (°C)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {/* Weight */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'weight']} label="Weight (kg)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {/* Height */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'height']} label="Height (cm)">
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          {/* Pain Level */}
          <Col xs={12} sm={8} md={6}>
            <Form.Item name={['vitals', 'pain']} label="Pain (0-10)">
              <InputNumber style={{ width: '100%' }} min={0} max={10} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default UpdateVitalsModal;
